import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AuthLayoutProps {
    children: React.ReactNode;
    heroVariant?: 'candidate' | 'company' | 'recruiter';
    title?: string;
    subtitle?: React.ReactNode;
}

const Logo = () => (
    <img src="/branding/logo_blue.svg" alt="Hush Logo" className="h-9 w-auto" />
);

const HERO_CONTENT = {
    candidate: [
        {
            title: "Let AI work for your career.",
            description: "Hush analyzes your experience, strengths, and preferences to find opportunities that truly align with you."
        },
        {
            title: "Your next opportunity starts here.",
            description: "Create your account, upload your resume, and let Hush's AI instantly find roles tailored to your experience."
        },
        {
            title: "Smarter matches. Faster hires.",
            description: "Hush uses AI to connect the right people with the right opportunities — no more endless scrolling or guessing."
        }
    ],
    company: [
        {
            title: "Meet Hush — where talent meets precision.",
            description: "We believe finding the right job or candidate shouldn't feel like a guessing game. Hush's AI interprets your goals."
        }
    ],
    recruiter: [
        {
            title: "Smarter hiring, human outcomes.",
            description: "Our AI understands skills, context, and culture fit — so you can focus on people, not paperwork."
        }
    ]
};

const AuthLayout: React.FC<AuthLayoutProps> = ({
    children,
    heroVariant = 'candidate',
    title,
    subtitle
}) => {
    const navigate = useNavigate();
    const [mounted, setMounted] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const slides = HERO_CONTENT[heroVariant] || HERO_CONTENT.candidate;

    useEffect(() => {
        setMounted(true);

        if (slides.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlide(prev => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [slides.length]);

    const nextSlide = () => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    };

    return (
        <div className="min-h-screen w-full flex flex-col lg:flex-row bg-[#F8F9FB] font-sans overflow-x-hidden">
            {/* Styles for animations */}
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&display=swap');
        
        body { font-family: 'Manrope', sans-serif; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes float {
          0% { transform: translateY(0px) rotate(25deg); }
          50% { transform: translateY(-20px) rotate(25deg); }
          100% { transform: translateY(0px) rotate(25deg); }
        }

        @keyframes pulse-slow {
          0% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
          100% { opacity: 0.8; transform: scale(1); }
        }

        .animate-fade-in-up { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-float { animation: float 15s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 8s ease-in-out infinite; }
      `}</style>

            {/* LEFT SIDE - FORM (45%) */}
            <div className="w-full lg:w-[45%] min-h-screen flex flex-col justify-center items-center p-6 sm:p-12 relative z-10 bg-[#F8F9FB]">
                <div className="w-full max-w-[480px] flex flex-col gap-8">

                    {/* Header Section */}
                    <div className="flex flex-col gap-6 mb-2">
                        {/* Logo */}
                        <div
                            className={`flex items-center gap-3 mb-4 transition-all duration-700 cursor-pointer ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            onClick={() => navigate('/')}
                        >
                            <Logo />
                        </div>

                        <div className={`space-y-3 transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            {title && (
                                <h1 className="text-[#015FA6] text-[44px] leading-tight font-bold tracking-tight">
                                    {title}
                                </h1>
                            )}
                            {subtitle && (
                                <div className="text-[#587Ca6] text-lg font-medium">
                                    {subtitle}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Form Content */}
                    <div className={`w-full transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        {children}
                    </div>

                    {/* Footer / Helper Text */}
                    <div className="mt-8 text-center animate-fade-in-up opacity-0" style={{ animationDelay: '600ms' }}>
                        <p className="text-sm text-[#015FA6]/50 font-medium">
                            Protected by reCAPTCHA and subject to the Google <a href="#" className="underline hover:text-[#015FA6]">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE - VISUAL (55%) */}
            <div className="hidden lg:flex w-full lg:w-[55%] min-h-screen bg-[#015FA6] relative overflow-hidden flex-col justify-end items-center pb-24 px-12 shadow-2xl z-20">

                {/* Modern Abstract Background */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#015FA6] to-[#004e8a]" />

                {/* Top Right Ellipse - Main decorative circle from Figma */}
                <div
                    className="absolute w-[803px] h-[793px] -top-[362px] -right-[100px] rounded-full pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465) 41.83%, rgba(237, 242, 247, 0) 100%)',
                        transform: 'rotate(26.84deg)',
                    }}
                />

                {/* Additional glowing orb for depth at bottom left */}
                <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-100px] bg-[#4FA3E0] rounded-full blur-[100px] opacity-20 pointer-events-none" />

                {/* Content Container */}
                <div className="relative z-10 max-w-[600px] text-center flex flex-col items-center gap-8">
                    <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '600ms' }}>
                        <h2 className="text-[#F8F9FB] text-5xl font-bold leading-[1.1] font-['Manrope'] mb-6 tracking-tight drop-shadow-sm">
                            {slides[currentSlide].title.split(' ').map((word, i, arr) => {
                                if (i > arr.length / 2) {
                                    return <span key={i} className="text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">{word} </span>
                                }
                                return word + ' ';
                            })}
                        </h2>
                        <p className="text-blue-100 text-xl font-medium leading-relaxed opacity-90">
                            {slides[currentSlide].description}
                        </p>
                    </div>

                    {/* Enhanced Carousel Navigation */}
                    {slides.length > 1 && (
                        <div className="flex items-center gap-8 mt-4 animate-fade-in-up opacity-0" style={{ animationDelay: '800ms' }}>
                            <button
                                onClick={prevSlide}
                                className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all active:scale-95 backdrop-blur-sm"
                            >
                                <ChevronLeft size={24} />
                            </button>

                            <div className="flex items-center gap-4 px-6 py-3 rounded-full bg-black/10 backdrop-blur-md border border-white/5">
                                {slides.map((_, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setCurrentSlide(index)}
                                        className={`
                                            rounded-full transition-all duration-300 cursor-pointer
                                            ${index === currentSlide
                                                ? 'w-3 h-3 bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]'
                                                : 'w-2 h-2 bg-white/40 hover:bg-white/70'
                                            }
                                        `}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={nextSlide}
                                className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all active:scale-95 backdrop-blur-sm"
                            >
                                <ChevronRight size={24} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthLayout;
